import React, {useState, useContext} from 'react';
import block from 'bem-cn-lite';
import {Button, Icon, Text, Tooltip} from '@gravity-ui/uikit';
import {Envelope, Copy, Comments} from '@gravity-ui/icons';
import ManagerPng from '@assets/avatar-manager.png';
import {AppContext} from '@app/context';
import {ClientDetails} from '@app/interfaces/ClientDetails';
import {openTalkMeSupport} from 'widgets';

import './ContactCard.scss';

const b = block('contact-card');

type ContactPerson = ClientDetails['mop'];

export const hasContactData = (contact?: ContactPerson | null) =>
    Boolean(contact?.name || contact?.phone || contact?.email);

interface ContactProps {
    children: React.ReactNode;
    copyText: string;
}

const Contact: React.FC<ContactProps> = ({children, copyText}) => {
    const [openTooltip, setOpenTooltip] = useState(false);
    return (
        <Tooltip
            content="Скопировано"
            placement="top-end"
            disabled={!openTooltip}
            openDelay={0}
            contentClassName={b('tooltip')}
            className={b('tooltip-container')}
        >
            <div
                className={b('copy')}
                role="button"
                tabIndex={0}
                onClick={() => {
                    setOpenTooltip(true);
                    navigator.clipboard.writeText(copyText);
                    setTimeout(() => setOpenTooltip(false), 1000);
                }}
                onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        setOpenTooltip(true);
                        navigator.clipboard.writeText(copyText);
                        setTimeout(() => setOpenTooltip(false), 1000);
                    }
                }}
            >
                {children}
            </div>
        </Tooltip>
    );
};

interface ContactBlockProps {
    contact: ContactPerson;
    title: React.ReactNode;
    avatarAlt: string;
    showSocialButton?: boolean;
}

const ContactBlock: React.FC<ContactBlockProps> = ({
    contact,
    title,
    avatarAlt,
    showSocialButton = false
}) => {
    const avatarSrc = contact?.photoUrl || ManagerPng;

    return (
        <div className={b('contact-container')}>
            <img
                src={avatarSrc}
                className={b('avatar')}
                alt={avatarAlt}
                onError={(e) => {
                    e.currentTarget.onerror = null; // защита от бесконечного цикла
                    e.currentTarget.src = ManagerPng;
                }}
            />

            <div className={b('contact-info')}>
                <Text variant="subheader-2">{title}</Text>
                <Text variant="body-2">{contact.name || ''}</Text>

                <Contact copyText={contact.email || ''}>
                    <Envelope />
                    <Text variant="body-1">{contact.email || ''}</Text>
                </Contact>

                <div className={b('contact-number')}>
                    <Contact copyText={contact.phone || ''}>
                        <Copy />
                        <Text variant="body-1">{contact.phone || ''}</Text>
                    </Contact>

                    {showSocialButton ? (
                        <Button
                            type="button"
                            size="m"
                            className={b('social-button')}
                            aria-label="Открыть чат поддержки"
                            onClick={() => openTalkMeSupport()}
                        >
                            <Icon data={Comments} size={16} />
                        </Button>
                    ) : null}
                </div>
            </div>
        </div>
    );
};

const ContactCard: React.FC = () => {
    const {
        state: {clientDetails}
    } = useContext(AppContext);

    const mop = clientDetails?.mop;
    const mrk = clientDetails?.mrk;
    const rop = clientDetails?.rop;

    const hasMop = hasContactData(mop);
    const hasMrk = hasContactData(mrk);
    const hasRop = hasContactData(rop);

    if (!hasMop && !hasMrk && !hasRop) {
        return null;
    }

    return (
        <div className={b()}>
            {hasMop && mop ? (
                <ContactBlock contact={mop} title="Ваш менеджер:" avatarAlt="Менеджер" />
            ) : null}

            {hasMop && (hasMrk || hasRop) ? <div className={b('separator')}></div> : null}

            {hasMrk && mrk ? (
                <ContactBlock
                    contact={mrk}
                    title={
                        <>
                            Специалист клиентской
                            <br />
                            поддержки:
                        </>
                    }
                    avatarAlt="Специалист клиентской поддержки"
                    showSocialButton
                />
            ) : null}

            {hasRop && rop ? (
                <ContactBlock
                    contact={rop}
                    title="Руководитель отдела продаж:"
                    avatarAlt="Руководитель отдела продаж"
                />
            ) : null}
        </div>
    );
};

export default ContactCard;
